import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/apiClient';

export interface GeneralConfig {
  website_name: string;
  maintenance_mode: boolean;
  logo_small: string | null;
  logo_big: string | null;
  favicon: string | null;
  footer_description?: string;
  footer_copyright?: string;
  footer_sections?: any;
  social_links?: any;
  app_download_url?: string | null;
  app_download_name?: string | null;
  show_app_download?: boolean;
  game_cards?: any;
  affiliate_overview?: any;
  affiliate_faq?: any;
  affiliate_terms?: string;
  hide_games_without_images?: boolean;
}

export interface Banner {
  id: number;
  type: string;
  image_url: string;
  link_url: string;
  is_active: boolean;
  sort_order: number;
}

export function useGeneralSettings() {
  const queryClient = useQueryClient();

  // Fetch Config
  const configQuery = useQuery({
    queryKey: ['websiteConfig'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/websiteconfig');
      const data = res.data?.data?.websiteConfig || {};
      const parsed: GeneralConfig = {
        website_name: data.website_name || '',
        maintenance_mode: data.maintenance_mode || false,
        logo_small: data.logo_small || null,
        logo_big: data.logo_big || null,
        favicon: data.favicon || null,
        footer_description: data.footer_description || '',
        footer_copyright: data.footer_copyright || '',
        footer_sections: typeof data.footer_sections === 'string' ? JSON.parse(data.footer_sections) : data.footer_sections,
        social_links: typeof data.social_links === 'string' ? JSON.parse(data.social_links) : data.social_links,
        app_download_url: data.app_download_url || null,
        app_download_name: data.app_download_name || null,
        show_app_download: data.show_app_download || false,
        game_cards: typeof data.game_cards === 'string' ? JSON.parse(data.game_cards) : data.game_cards,
        affiliate_overview: typeof data.affiliate_overview === 'string' ? JSON.parse(data.affiliate_overview) : data.affiliate_overview,
        affiliate_faq: typeof data.affiliate_faq === 'string' ? JSON.parse(data.affiliate_faq) : data.affiliate_faq,
        affiliate_terms: data.affiliate_terms || '',
        hide_games_without_images: data.hide_games_without_images || false,
      };
      return parsed;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch Banners
  const bannersQuery = useQuery({
    queryKey: ['banners'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/website-banners', {
        headers: { authorization: localStorage.getItem('token') }
      });
      return res.data.data as Banner[];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Update Config
  const updateConfigMutation = useMutation({
    mutationFn: async (payload: { config: GeneralConfig; logoSmallFile: File | null; logoBigFile: File | null; faviconFile: File | null; appFile?: File | null }) => {
      const { config, logoSmallFile, logoBigFile, faviconFile, appFile } = payload;
      
      await apiClient.post('/admin/updatewebsiteconfig', {
        websiteConfig: {
          website_name: config.website_name,
          maintenance_mode: config.maintenance_mode,
          react_app_name: 'Winsmash',
          react_app_title: 'Winsmash',
          react_app_link: 'Winsmash',
          footer_description: config.footer_description,
          footer_copyright: config.footer_copyright,
          footer_sections: config.footer_sections,
          social_links: config.social_links,
          show_app_download: config.show_app_download,
          app_download_url: config.app_download_url,
          app_download_name: config.app_download_name,
          game_cards: config.game_cards,
          affiliate_overview: config.affiliate_overview,
          affiliate_faq: config.affiliate_faq,
          affiliate_terms: config.affiliate_terms,
          hide_games_without_images: config.hide_games_without_images,
        },
        contactChannels: { dummy: [{ platform: 'dummy', value: 'dummy', is_active: false }] }
      });

      if (logoSmallFile || logoBigFile || faviconFile || appFile) {
        const formData = new FormData();
        if (logoSmallFile) formData.append('logo_small', logoSmallFile);
        if (logoBigFile) formData.append('logo_big', logoBigFile);
        if (faviconFile) formData.append('favicon', faviconFile);
        if (appFile) formData.append('app_file', appFile);

        await apiClient.post('/admin/upload-logos', formData, {
          headers: {
            authorization: localStorage.getItem('token') || '',
          }
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websiteConfig'] });
    }
  });

  // Add Banner
  const addBannerMutation = useMutation({
    mutationFn: async (payload: { newBanner: { type: string; link_url: string; sort_order: number }; bannerFile: File }) => {
      const formData = new FormData();
      formData.append('type', payload.newBanner.type);
      formData.append('link_url', payload.newBanner.link_url);
      formData.append('sort_order', payload.newBanner.sort_order.toString());
      formData.append('image', payload.bannerFile);

      await apiClient.post('/admin/website-banners', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          authorization: localStorage.getItem('token') 
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
    }
  });

  // Delete Banner
  const deleteBannerMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/admin/website-banners/${id}`, {
        headers: { authorization: localStorage.getItem('token') }
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['banners'] })
  });

  // Toggle Banner Status
  const toggleBannerMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.patch(`/admin/website-banners/${id}/status`, {}, {
        headers: { authorization: localStorage.getItem('token') }
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['banners'] })
  });

  // Update Banner
  const updateBannerMutation = useMutation({
    mutationFn: async (payload: { id: number; updatedBanner: { type: string; link_url: string; sort_order: number; is_active?: boolean }; bannerFile?: File | null }) => {
      const formData = new FormData();
      formData.append('type', payload.updatedBanner.type);
      formData.append('link_url', payload.updatedBanner.link_url);
      formData.append('sort_order', payload.updatedBanner.sort_order.toString());
      if (payload.updatedBanner.is_active !== undefined) {
        formData.append('is_active', payload.updatedBanner.is_active.toString());
      }
      if (payload.bannerFile) {
        formData.append('image', payload.bannerFile);
      }

      await apiClient.put(`/admin/website-banners/${payload.id}`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          authorization: localStorage.getItem('token') 
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
    }
  });

  return {
    configQuery,
    bannersQuery,
    updateConfigMutation,
    addBannerMutation,
    deleteBannerMutation,
    toggleBannerMutation,
    updateBannerMutation
  };
}
